import { utils, ColorTypes, actions } from "@omegagrid/core";
import { CellModel, ColumnSource, RequestFilters, RowSource } from "@omegagrid/grid";
import { msg } from "@omegagrid/localize";
import rp, { GridEditorAdapter, GridEditorOptions, RowData, SettingsItem } from "@reporting/rp-app-host";
import { AccountingRights } from "@reporting/rp-types/core";

// 'user' = personal editor: no scopeId edits your own settings (from the user menu), a username in
//   scopeId edits THAT user's personal (user*) settings as an admin (from the users grid);
// 'accounting' / 'group' = single-scope editors opened for a specific accounting/group;
// 'role' = editor opened from the role grid (offers the role* scopes of one access role).
export type SettingsScope = 'user' | 'accounting' | 'group' | 'role';

export class SettingsGridEditorAdapter extends GridEditorAdapter {

	private _columns: ColumnSource[];
	private _showHidden = false;

	constructor(
		private scope: SettingsScope = 'user',
		// number for accounting/group/role; username string when editing another user's user* settings
		private scopeId: number | string = null,
	) { super(); }

	private get isUserScope() { return this.scope === 'user'; }
	private get isRoleScope() { return this.scope === 'role'; }
	// admin editing another user's personal settings (user scope with a target username)
	private get isUserTarget() { return this.isUserScope && this.scopeId != null; }

	// 'user_accounting' -> 'user'-'accounting' etc., matching the scope dropdown labels
	private scopeLabel(scope: string): string {
		const [base, sub] = scope.split('_');
		return sub ? `${msg(base)}-${msg(sub)}` : msg(scope);
	}

	async getColumns(): Promise<ColumnSource[]> {
		if (this._columns) return this._columns;

		// the user/role editors offer their scope families; accounting/group editors are locked to their single scope
		const scopes: {k: string, v: string}[] = this.isUserScope ? [
			{k: 'user', v: msg('user')},
			{k: 'user_accounting', v: `${msg('user')}-${msg('accounting')}`},
			{k: 'user_group', v: `${msg('user')}-${msg('group')}`},
		] : this.isRoleScope ? [
			{k: 'role', v: msg('role')},
			{k: 'role_accounting', v: `${msg('role')}-${msg('accounting')}`},
		] : [
			{k: this.scope, v: msg(this.scope)},
		];

		this._columns = [{
			data: 'id',
			header: msg('Setting'),
			t: 't',
			e: false,
			w: 200
		}, {
			data: 'value',
			header: msg('Value'),
			t: 't',
			e: true,
			w: 300
		}, {
			data: 'scope',
			header: msg('Scope'),
			t: 's',
			e: this.isUserScope || this.isRoleScope,
			w: 150,
			content: scopes
		}, {
			data: 'description',
			header: msg('Description'),
			t: 't',
			e: false,
			w: 400
		}];
		return this._columns;
	}

	private getValue(v: any) {
		const value = typeof v === 'function' ? v() : v;
		return utils.isObject(value) || Array.isArray(value) ? JSON.stringify(value) : value;
	}

	async getRows(filters?: RequestFilters): Promise<RowSource[]> {
		const params: Record<string, any> = { scopes: 1 };
		// admin editing another user's personal settings -> dedicated per-user endpoint (gated by findUser)
		let path = this.isUserTarget ? `${this.scopeId}/settings` : `settings`;
		if (this.scope === 'accounting') {
			// ?accounting=<id> switches the ms context to (and access-checks) the target accounting
			params.settings_scope = 'accounting';
			params.accounting = this.scopeId;
		} else if (this.scope === 'group') {
			params.settings_scope = 'group';
			params.settings_scope_id = this.scopeId;
		} else if (this.scope === 'role') {
			params.settings_scope = 'role';
			params.settings_scope_id = this.scopeId;
		}
		const values = await rp.ms('users').get<Record<string, {value: any, scope: string}>>(path, params);
		return [...rp.settings.items.values()]
			.filter(item => this._showHidden || !item.hidden)
			.map(item => {
				const stored = values[item.id];
				const origin = stored?.scope;
				const inherited = this.isUserScope && ['accounting', 'group', 'role', 'role_accounting'].includes(origin);
				const comment =
					inherited ? { text: `${msg('Inherited from')}: ${this.scopeLabel(origin)}` } :
					(this.isUserScope && !stored) ? { text: msg('Default') } :
					null;
				return {
					data: {
						id: item.id,
						description: item.description,
						value: {
							// user editor shows the effective value (falling back to the default); the scope editors
							// show only what is explicitly stored at that layer (blank = inherited)
							v: this.getValue(stored?.value ?? (this.isUserScope ? item.defaultValue : null)),
							t: item.type || 't',
							content: typeof item.content === 'function' ? item.content() : item.content,
							...(comment ? { comment } : {}),
						},
						scope: this.isUserScope ? (inherited ? 'user' : (origin || 'user')) :
							this.isRoleScope ? (origin || 'role') :
							this.scope,
						_state: 'clean',
					}
				};
			});
	}

	getOptions(): Partial<GridEditorOptions> {
		return this.createOptions({
			numberFormat: '0',
			colLetters: false,
			columnSorting: true,
			filters: true,
			pageSize: 0,
			refreshAfterSave: true,
			saveMode: 'edited_rows',
			toolbar: {
				new: { enabled: false },
			},
			// hidden settings are shown only to Admins
			customToolbar: rp.userProvider.hasRight(AccountingRights.Admin) ? [{
				type: 'togglebutton',
				id: 'showHidden',
				icon: { icon: 'eye' },
				text: msg('Show hidden'),
				color: ColorTypes.Accent,
			}] : [],
			// exportTypes : ['csv', 'xlsx'],
		});
	}

	onAction(action: string, cell: CellModel, event: actions.ActionEvent = null) {
		switch (action) {
			case 'showHidden':
				this._showHidden = event.action.value as boolean;
				this.editor.refresh();
				break;
		}
	}

	// A secure value is delivered masked by the backend (first char + *** + last char,
	// or *** for very short values). Detect that shape so we never save it back.
	private isMaskedSecret(id: string, value: any): boolean {
		if (!rp.settings.items.get(id)?.secure) return false;
		return typeof value === 'string' && (value === '***' || /^.\*\*\*.$/s.test(value));
	}

	async save(rowData: RowData[]) {
		const values: { id: string, value: any, scope: string, scopeId?: number | string }[] = rowData
			.map(row => ({
				id: row.data.id as string,
				value: row.data.value,
				// accounting/group editors are locked to their scope; the user/role editors take the per-row scope
				scope: this.isUserScope ? (row.data.scope as string || 'user') :
					this.isRoleScope ? (row.data.scope as string || 'role') :
					this.scope,
				// group/role target an explicit id; accounting rides the ?accounting context, and the
				// per-user endpoint (isUserTarget) carries the target in its URL - user* layers default server-side
				...(this.scope === 'group' || this.scope === 'role' ? { scopeId: this.scopeId } : {}),
			}))
			// Secure settings are write-only: skip rows whose value is still the mask
			// so the real secret is never overwritten by its masked placeholder.
			.filter(v => !this.isMaskedSecret(v.id, v.value));

		if (!values.length) return;
		const path = this.isUserTarget ? `${this.scopeId}/settings` : `settings`;
		// accounting scope always carries a numeric id
		const params = this.scope === 'accounting' ? { accounting: this.scopeId as number } : undefined;
		await rp.ms('users').patch(path, values, params);
	}

}
